import React, { useRef, useState } from 'react';
import Button from '../../components/ui/Button';
import StatusBanner from '../../components/ui/StatusBanner';
import {
  downloadEmployeeImportTemplate,
  previewEmployeeImport,
  commitEmployeeImport
} from '../../services/employeeImportApi';

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function ImportSummary({ summary }) {
  return (
    <div className="employee-import-summary" data-testid="employee-import-summary">
      <div className="employee-import-summary__stat">
        <span>Total rows</span>
        <strong>{summary.totalRows}</strong>
      </div>
      <div className="employee-import-summary__stat">
        <span>Valid rows</span>
        <strong>{summary.validRows}</strong>
      </div>
      <div className="employee-import-summary__stat">
        <span>Invalid rows</span>
        <strong>{summary.invalidRows}</strong>
      </div>
      <div className="employee-import-summary__stat">
        <span>Employee IDs to generate</span>
        <strong>{summary.employeeIdsToGenerate}</strong>
      </div>
      <div className="employee-import-summary__stat">
        <span>Locations affected</span>
        <strong>{summary.locationsAffected}</strong>
      </div>
    </div>
  );
}

function ImportEmployeesModal({ onClose, onImported, onSetupEnrollment }) {
  const [step, setStep] = useState('upload'); // 'upload' | 'preview' | 'result'
  const [fileName, setFileName] = useState('');
  const [csvText, setCsvText] = useState('');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);
  const [error, setError] = useState('');
  const commitInFlightRef = useRef(false);

  const handleDownloadTemplate = async () => {
    setIsDownloadingTemplate(true);
    setError('');
    try {
      const { blob, filename } = await downloadEmployeeImportTemplate();
      triggerBlobDownload(blob, filename);
    } catch (err) {
      setError(err?.message || 'Unable to download the employee import template.');
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setFileName(file.name);
    setCsvText(text);
    setPreview(null);
    setError('');
  };

  const handleValidate = async () => {
    if (!csvText) {
      setError('Choose a CSV file before validating.');
      return;
    }
    setIsValidating(true);
    setError('');
    try {
      const payload = await previewEmployeeImport(csvText);
      setPreview(payload);
      setStep('preview');
    } catch (err) {
      setError(err?.message || 'Unable to validate the CSV.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleConfirmImport = async () => {
    if (commitInFlightRef.current) return;
    if (!preview?.allValid) return;
    commitInFlightRef.current = true;
    setIsCommitting(true);
    setError('');
    try {
      const payload = await commitEmployeeImport({ csvText, fingerprint: preview.fingerprint });
      setResult(payload);
      setStep('result');
    } catch (err) {
      setError(err?.message || 'Unable to import employees.');
    } finally {
      setIsCommitting(false);
      commitInFlightRef.current = false;
    }
  };

  const handleChooseDifferentFile = () => {
    setStep('upload');
    setPreview(null);
    setError('');
  };

  const invalidRows = (preview?.rows || []).filter(row => row.status === 'error');

  return (
    <div className="employee-hub-modal-backdrop" role="presentation">
      <div className="employee-hub-modal" role="dialog" aria-modal="true" aria-labelledby="import-employees-title">
        <div className="employee-hub-modal__header">
          <div>
            <h2 id="import-employees-title">Import Employees</h2>
            <p>Bulk-create standalone employees from a CSV file. Nothing is created until you confirm the preview.</p>
          </div>
          <button type="button" className="employee-hub-modal__close" onClick={onClose} aria-label="Close import employees">×</button>
        </div>

        {error ? <StatusBanner kind="error" message={error} live /> : null}

        {step === 'upload' ? (
          <div className="employee-import-step" data-testid="employee-import-step-upload">
            <ol className="employee-import-steps">
              <li>
                <Button variant="secondary" onClick={handleDownloadTemplate} loading={isDownloadingTemplate} loadingLabel="Preparing template…">
                  Download Template
                </Button>
              </li>
              <li>
                <label className="director-upload-card">
                  <span>{fileName || 'Choose CSV File'}</span>
                  <input type="file" accept=".csv,text/csv" onChange={handleFileChange} data-testid="employee-import-file-input" />
                </label>
              </li>
            </ol>
            <div className="employee-hub-modal__actions">
              <Button variant="primary" onClick={handleValidate} disabled={!csvText} loading={isValidating} loadingLabel="Validating…" data-testid="employee-import-validate">
                Validate
              </Button>
            </div>
          </div>
        ) : null}

        {step === 'preview' && preview ? (
          <div className="employee-import-step" data-testid="employee-import-step-preview">
            <ImportSummary summary={preview.summary} />

            {invalidRows.length > 0 ? (
              <div className="employee-import-errors" data-testid="employee-import-row-errors">
                <strong>Row errors</strong>
                <ul className="director-bullet-list">
                  {invalidRows.map(row => (
                    <li key={row.rowNumber}>
                      Row {row.rowNumber}{row.identifier ? ` (${row.identifier})` : ''}:{' '}
                      {row.errors.map(e => e.message).join(' ')}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <StatusBanner kind="success" message="Every row is valid. Confirm to create these employees." />
            )}

            <div className="employee-hub-modal__actions">
              <Button variant="secondary" onClick={handleChooseDifferentFile} disabled={isCommitting}>
                Choose a Different File
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmImport}
                disabled={!preview.allValid || isCommitting}
                loading={isCommitting}
                loadingLabel="Importing…"
                data-testid="employee-import-confirm"
              >
                Confirm Import
              </Button>
            </div>
          </div>
        ) : null}

        {step === 'result' && result ? (
          <div className="employee-import-step" data-testid="employee-import-step-result">
            <StatusBanner kind="success" message={`Employees imported: ${result.summary.committed}.`} live />
            <div className="employee-import-summary" data-testid="employee-import-result-summary">
              <div className="employee-import-summary__stat">
                <span>Employees imported</span>
                <strong>{result.summary.committed}</strong>
              </div>
              <div className="employee-import-summary__stat">
                <span>Employee IDs generated</span>
                <strong>{result.summary.employeeIdsGenerated}</strong>
              </div>
              <div className="employee-import-summary__stat">
                <span>Locations affected</span>
                <strong>{result.summary.locationsAffected}</strong>
              </div>
              <div className="employee-import-summary__stat"><span>Kiosk ready</span><strong>{result.summary.kioskReady}</strong></div>
              <div className="employee-import-summary__stat"><span>PIN/enrollment required</span><strong>{result.summary.pinEnrollmentRequired}</strong></div>
            </div>
            <div className="employee-hub-modal__actions">
              <Button variant="secondary" onClick={() => onImported?.(result)} data-testid="employee-import-done">Return to Employee Directory</Button>
              <Button variant="primary" onClick={() => onSetupEnrollment?.(result)} data-testid="employee-import-enrollment-handoff">Set Up Kiosk Enrollment</Button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default ImportEmployeesModal;
